import { Box, Container, Stack, Typography, styled } from "@mui/material";
import {
  Button,
  Color,
  RadioButton,
  SimpleInput,
  Tooltip,
} from "@axismaxlife/unified-ui-components-library";
import { InfoIcon } from "@axismaxlife/unified-ui-components-library/dist/systemIcons";
import { useContext, useEffect, useState } from "react";
import { PageTitle } from "../../commanComponents/PageTitle";
import { String } from "../../constants/Strings";
import { JourneyProgressStepper } from "../../commanComponents/JourneyProgressStepper";
import { numberToWords } from "../../logic/NumberToWord";
import { useLocation, useNavigate } from "react-router-dom";
import { AuthContext } from "../../Hooks/context/AuthContext";
import { AppToast } from "../../commanComponents/AppToast";
import useFinancialDetails from "../../Hooks/CustomHooks/useFinancialDetails";
import { useLocalStorage } from "../../logic/LocalStorage/LocalStorage";
import { gaCustomEvent } from "../../logic/CommonFunctions";
import { OTPVerifyContext } from "../../Hooks/context/OTPVeriryContext";
import { colors, fonts } from "../../style";

const StyledQuestionTypography = styled(Typography)(() => ({
  color: colors.NeutralDark700,
  fontSize: "16px",
  fontFamily: fonts.notoSans,
  fontWeight: 500,
}));

const toDigits = (raw) => `${raw ?? ""}`.replace(/[^0-9]/g, "");

const toAmountValue = (amount) => {
  const digits = toDigits(amount);
  return digits === "0" ? "" : digits;
};

const amountInWords = (amount) => {
  const digits = toDigits(amount);
  if (!digits || Number(digits) === 0) return "";
  const words = numberToWords(digits);
  return words ? `₹ ${words}` : "";
};

const requiredLabel = (text) => (
  <>
    {text}{" "}
    <Box component="span" aria-hidden="true" sx={{ color: Color.Text.Warning }}>
      *
    </Box>
  </>
);

const INVESTMENT_STYLES = [
  {
    value: "Aggressive",
    label: String.Monthlyincome.ButtonAggressive,
    description: String.Monthlyincome.investmentStyleAggressive,
  },
  {
    value: "Moderate",
    label: String.Monthlyincome.ButtonModerate,
    description: String.Monthlyincome.investmentStyleModerate,
  },
  {
    value: "Conservative",
    label: String.Monthlyincome.ButtonConversion,
    description: String.Monthlyincome.investmentStyleConservative,
  },
];

const [FIRST_INVESTMENT_STYLE, ...OTHER_INVESTMENT_STYLES] = INVESTMENT_STYLES;

const InvestmentStyleLegend = () => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      gap: "12px",
      fontSize: "14px",
      fontFamily: fonts.notoSans,
    }}
  >
    {OTHER_INVESTMENT_STYLES.map((style) => (
      <Box key={style.value}>
        <Box sx={{ fontWeight: 700, fontSize: "14px" }}>{style.label}</Box>
        <Box sx={{ mt: "2px", fontSize: "14px", fontWeight: 400 }}>
          {style.description}
        </Box>
      </Box>
    ))}
  </Box>
);

const FinanacialQuestion = () => {
  const [activeButton, setActiveButton] = useState(null);
  const {
    monthlySalary,
    monthlySaving,
    saveFinancialData,
    setmonthlySalary,
    setmonthlySaving,
    snackbarOpen,
    snackbarmessage,
    setSnackbarOpen,
  } = useFinancialDetails();
  const { authTokens } = useContext(AuthContext);
  const { verifyData } = useContext(OTPVerifyContext);
  const { retrieveLocalFinancialData } = useLocalStorage();
  const navigation = useNavigate();
  const location = useLocation();
  const { userId } = authTokens;

  const handleClick = (buttonId) => {
    gaCustomEvent(
      String.gaEventName.acrPortfolio,
      String.gaEventClickName.acrPortfolioInvestmentStyleEntered,
      verifyData.customerId,
    );
    setActiveButton(buttonId);
  };

  const isFormComplete = Boolean(
    toDigits(monthlySalary) && toDigits(monthlySaving) && activeButton,
  );
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  useEffect(() => {
    const retrieveSavedData = async () => {
      try {
        const savedData = await retrieveLocalFinancialData(
          `financialData-${userId}`,
        );
        if (savedData) {
          setmonthlySalary(savedData.monthlySalary);
          setmonthlySaving(savedData.monthlySaving);
          setActiveButton(savedData.investmentStyle);
        }
      } catch (error) { }
    };
    retrieveSavedData();
  }, []);

  const handleContineu = async (e) => {
    e.preventDefault();

    gaCustomEvent(
      String.gaEventName.acrPortfolio,
      String.gaEventClickName.acrPortfolioContinue,
      verifyData.customerId,
    );

    const success = await saveFinancialData(userId, activeButton);
    if (success) {
      setTimeout(() => {
        navigation(String.navigations.goal);
      }, 2000);
    }
  };

  return (
    <Container>
      <PageTitle />
      <JourneyProgressStepper />
      <Box sx={{ mt: "30px" }}>
        <StyledQuestionTypography sx={{ mb: "8px" }}>
          {requiredLabel(String.Monthlyincome.question1)}
        </StyledQuestionTypography>
        <SimpleInput
          name="monthlySalary"
          type="number"
          indianNumberFormat
          prefix="₹"
          width="100%"
          label={String.Monthlyincome.amountPlaceholder}
          placeholder={String.Monthlyincome.amountPlaceholder}
          value={toAmountValue(monthlySalary)}
          hintText={amountInWords(monthlySalary)}
          onChange={(e) => {
            const value = toDigits(e.target.value);
            gaCustomEvent(
              String.gaEventName.acrPortfolio,
              String.gaEventClickName.acrPortfolioIncomeEntered,
              verifyData.customerId,
            );
            setmonthlySalary(value);
            if (Number(value)) {
              setmonthlySaving(((Number(value) * 30) / 100).toFixed(0));
            } else {
              setmonthlySaving("");
            }
          }}
        />
      </Box>
      <Box sx={{ mt: "24px" }}>
        <StyledQuestionTypography sx={{ mb: "8px" }}>
          {requiredLabel(String.Monthlyincome.question2)}
        </StyledQuestionTypography>
        <SimpleInput
          name="monthlySaving"
          type="number"
          indianNumberFormat
          prefix="₹"
          width="100%"
          label={String.Monthlyincome.amountPlaceholder}
          placeholder={String.Monthlyincome.amountPlaceholder}
          value={toAmountValue(monthlySaving)}
          hintText={amountInWords(monthlySaving)}
          onChange={(e) => {
            gaCustomEvent(
              String.gaEventName.acrPortfolio,
              String.gaEventClickName.acrPortfolioSavingsEntered,
              verifyData.customerId,
            );
            setmonthlySaving(toDigits(e.target.value));
          }}
        />
      </Box>
      <Box sx={{ mt: "30px", display: "flex", flexDirection: "column" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <StyledQuestionTypography>
            {requiredLabel(String.Monthlyincome.question3)}
          </StyledQuestionTypography>
          <Tooltip
            device="mobile"
            mode="information"
            trigger="click"
            placement="bottom-end"
            size="large"
            showTitle
            title={FIRST_INVESTMENT_STYLE.label}
            showBodyText
            bodyText={FIRST_INVESTMENT_STYLE.description}
            showCloseButton
            slotContent={<InvestmentStyleLegend />}
          >
            <Box
              component="span"
              sx={{ display: "flex", cursor: "pointer", flexShrink: 0 }}
            >
              <InfoIcon size="default" fillColor={Color.Icons.Default} />
            </Box>
          </Tooltip>
        </Box>

        <Stack spacing={1.5} sx={{ mt: "16px" }}>
          {INVESTMENT_STYLES.map((style) => (
            <Box
              sx={{
                border: 1,
                height: "56px",
                borderColor: colors.lavendershade,
                borderRadius: "8px",
                p: "16px",
              }}
            >
              <RadioButton
                key={style.value}
                name="investmentStyle"
                value={style.value}
                label={style.label}
                checked={activeButton === style.value}
                onChange={handleClick}
              />
            </Box>
          ))}
        </Stack>
      </Box>
      <Box sx={{ mt: "40px", mb: "24px" }}>
        <Button
          buttonType="primary"
          width="100%"
          disabled={!isFormComplete}
          onClick={handleContineu}
        >
          {String.Monthlyincome.proceedButton}
        </Button>
      </Box>
      <AppToast
        open={snackbarOpen}
        handleClose={() => {
          setSnackbarOpen(false);
        }}
        message={snackbarmessage.message}
        severity={snackbarmessage.severity}
      />
    </Container>
  );
};

export default FinanacialQuestion;
